import React, { useState, useEffect } from 'react';
import {
    BookOpen, Brain, CheckSquare, FileText, Upload, PenTool,
    ChevronRight, ChevronDown, Check, Lock, Circle, Loader2, Save, FormInput,
    Video, Minus, Info, Lightbulb, AlertTriangle, Heading
} from 'lucide-react';
import QuizViewer from './QuizViewer';
import TrainingViewer from './TrainingViewer';
import ChecklistViewer from './ChecklistViewer';
import ProofFileUpload from './ProofFileUpload';
import SignatureSection from './SignatureSection';
import FormFieldsViewer, { validateFormFields, packageFormResponses } from './FormFieldsViewer';
import SignatureAuditDisplay from './SignatureAuditDisplay';
import { generateSignatureAuditTrail } from '../../utils/signatureAudit';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SECTION_ICONS = {
    training: BookOpen,
    quiz: Brain,
    checklist: CheckSquare,
    form_fields: FormInput,
    content: FileText,
    video: Video,
    divider: Minus,
    callout: Info,
    heading: Heading,
    proof_upload: Upload,
    signature: PenTool
};

// Separate component for signature section to handle audit trail capture
const SignatureSectionViewer = ({
    content,
    signatureData,
    signatureAudit,
    onSignatureChange,
    onComplete,
    isAdmin,
    isCompleted,
    isSubmitting,
    signerInfo
}) => {
    const [isCapturingAudit, setIsCapturingAudit] = useState(false);

    const handleSignatureSubmit = async () => {
        if (!signatureData || signatureData.length < 3) {
            toast.error('Please provide a valid signature');
            return;
        }

        setIsCapturingAudit(true);
        try {
            const auditTrail = await generateSignatureAuditTrail(signerInfo);
            onComplete({
                signature: signatureData,
                signature_audit: auditTrail,
                signed_at: auditTrail.signed_at
            });
        } catch (error) {
            console.error('Error capturing audit trail:', error);
            onComplete({
                signature: signatureData,
                signature_audit: {
                    signed_at: new Date().toISOString(),
                    error: 'Audit capture failed'
                },
                signed_at: new Date().toISOString()
            });
        } finally {
            setIsCapturingAudit(false);
        }
    };

    if (isAdmin && isCompleted && signatureData) {
        return (
            <div className="space-y-6">
                <SignatureAuditDisplay
                    signatureData={signatureData}
                    signatureAudit={signatureAudit}
                    signerName={signerInfo?.name}
                    signerEmail={signerInfo?.email}
                    showFullDetails={true}
                />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <SignatureSection
                signatureData={signatureData}
                onSignatureChange={onSignatureChange}
                agreementText={content?.acknowledgment_text || "I acknowledge that I have read and understood the information provided and agree to comply with it."}
                isSubmitting={isSubmitting || isCapturingAudit}
                readOnly={isAdmin || isCompleted}
            />

            {!isAdmin && (
                <div className="flex justify-end">
                    {isCompleted ? (
                        <span className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium">
                            <Check className="w-4 h-4" /> Signed
                        </span>
                    ) : (
                        <button
                            onClick={handleSignatureSubmit}
                            disabled={isSubmitting || isCapturingAudit || !signatureData || signatureData.length < 3}
                            className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {isSubmitting || isCapturingAudit ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Signing...</>
                            ) : (
                                <><PenTool className="w-4 h-4" /> Submit Signature</>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Separate component for form fields section
const FormFieldsSectionViewer = ({
    content,
    formResponses,
    onResponsesChange,
    onComplete,
    isAdmin,
    isCompleted,
    isSubmitting
}) => {
    const [showValidation, setShowValidation] = useState(false);
    const validationErrors = validateFormFields(content?.fields || [], formResponses);

    const handleFormSubmit = () => {
        const errors = validateFormFields(content?.fields || [], formResponses);
        if (Object.keys(errors).length > 0) {
            setShowValidation(true);
            toast.error('Please fill in all required fields');
            return;
        }

        const packagedResponses = packageFormResponses(content?.fields || [], formResponses);
        onComplete({
            responses: formResponses,
            packaged_responses: packagedResponses,
            submitted_at: new Date().toISOString()
        });
    };

    return (
        <div className="space-y-4">
            <FormFieldsViewer
                content={content}
                responses={formResponses}
                onResponsesChange={onResponsesChange}
                readOnly={isAdmin || isCompleted}
                showValidation={showValidation}
                validationErrors={validationErrors}
            />

            {!isAdmin && (
                <div className="flex justify-end">
                    {isCompleted ? (
                        <span className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium">
                            <Check className="w-4 h-4" /> Submitted
                        </span>
                    ) : (
                        <button
                            onClick={handleFormSubmit}
                            disabled={isSubmitting}
                            className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 transition-colors flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                            ) : (
                                <><Check className="w-4 h-4" /> Submit Form</>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const CombinedTaskViewer = ({
    task,
    assignment,
    employeeInfo = null,
    onSectionComplete,
    onAllComplete,
    previewMode = false,
    isAdmin = false
}) => {
    const [sections, setSections] = useState([]);
    const [sectionProgress, setSectionProgress] = useState({});
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sectionResponses, setSectionResponses] = useState({});
    const [collapsedSections, setCollapsedSections] = useState({});

    useEffect(() => {
        fetchSectionsAndProgress();
    }, [task?.id]);

    const fetchSectionsAndProgress = async () => {
        if (!task?.id) {
            if (task?.sections && task.sections.length > 0) {
                setSections(task.sections);
            }
            setIsLoading(false);
            return;
        }

        const targetTaskId = task.task_id || task.id;
        setIsLoading(true);
        try {
            if (task.sections && task.sections.length > 0) {
                setSections(task.sections);
            } else {
                const sectionsRes = await api.get(`/tasks/${targetTaskId}/sections`);
                if (sectionsRes.data.success) {
                    setSections(sectionsRes.data.sections);
                }
            }

            if (assignment?.section_progress) {
                setSectionProgress(assignment.section_progress);
                const initialCollapsed = {};
                Object.entries(assignment.section_progress).forEach(([id, prog]) => {
                    if (prog?.status === 'completed') initialCollapsed[id] = true;
                });
                setCollapsedSections(initialCollapsed);
                const isFinished = ['completed', 'pending_review', 'failed'].includes(assignment.status);
                setCurrentSectionIndex(isFinished ? 0 : (assignment.current_section_index || 0));
            } else if (!previewMode && !isAdmin) {
                const progressRes = await api.get(`/tasks/employee/${targetTaskId}/section-progress`);
                if (progressRes.data.success) {
                    const progress = progressRes.data.section_progress || {};
                    setSectionProgress(progress);
                    const initialCollapsed = {};
                    Object.entries(progress).forEach(([id, prog]) => {
                        if (prog?.status === 'completed') initialCollapsed[id] = true;
                    });
                    setCollapsedSections(initialCollapsed);
                    const isFinished = ['completed', 'pending_review', 'failed'].includes(assignment?.status);
                    setCurrentSectionIndex(isFinished ? 0 : (progressRes.data.current_section_index || 0));
                }
            }
        } catch (error) {
            console.error('Error fetching sections or progress:', error);
            if (task.sections && sections.length === 0) {
                setSections(task.sections);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const isSectionCompleted = (sectionId) => {
        return sectionProgress[sectionId]?.status === 'completed';
    };

    const isSectionLocked = (index) => {
        if (index === 0 || isAdmin) return false;
        for (let i = 0; i < index; i++) {
            const prevSection = sections[i];
            if (prevSection.is_required && !isSectionCompleted(prevSection.id)) {
                return true;
            }
        }
        return false;
    };

    const canAccessSection = (index) => !isSectionLocked(index);

    const handleSectionClick = (index) => {
        if (canAccessSection(index)) setCurrentSectionIndex(index);
    };

    const handleSectionComplete = async (sectionId, responseData, score) => {
        setIsSubmitting(true);
        try {
            const targetTaskId = task.task_id || task.id;
            const response = await api.post(`/tasks/employee/${targetTaskId}/section/${sectionId}/complete`, {
                response_data: responseData,
                score
            });

            if (response.data.success) {
                setSectionProgress(response.data.section_progress);
                setCollapsedSections(prev => ({ ...prev, [sectionId]: true }));

                if (response.data.all_complete) {
                    const completionResults = response.data.completion_results;
                    if (completionResults?.requires_review) {
                        toast.success('All sections completed! Submitted for review.');
                    } else {
                        toast.success('All sections completed! Task finished.');
                    }
                    onAllComplete?.(completionResults);
                } else {
                    toast.success('Section completed!');
                    if (response.data.next_section_index !== null) {
                        setCurrentSectionIndex(response.data.next_section_index);
                    }
                    onSectionComplete?.(sectionId, responseData);
                }
            }
        } catch (error) {
            console.error('Error completing section:', error);
            toast.error(error.response?.data?.message || 'Failed to complete section');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResponseChange = (sectionId, data) => {
        setSectionResponses(prev => ({ ...prev, [sectionId]: data }));
    };

    // Get short metadata for a section
    const getSectionMeta = (section) => {
        try {
            const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
            if (section.section_type === 'quiz') return `${parsed.questions?.length || 0} questions`;
            if (section.section_type === 'checklist') return `${parsed.items?.length || 0} items`;
            if (section.section_type === 'training') return `${parsed.sections?.length || 0} parts`;
            if (section.section_type === 'form_fields') return `${parsed.fields?.length || 0} fields`;
        } catch (e) { /* ignore */ }
        return null;
    };

    const renderSectionContent = (section) => {
        let content;
        try {
            content = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
        } catch (e) {
            content = section.content;
        }

        const isCompleted = isSectionCompleted(section.id);
        const previousResponse = sectionProgress[section.id]?.response_data;

        switch (section.section_type) {
            case 'quiz':
                return (
                    <QuizViewer
                        content={content}
                        onComplete={(answers, score) => handleSectionComplete(section.id, answers, score)}
                        isSubmitting={isSubmitting}
                        isCompleted={isCompleted}
                        previousResponse={previousResponse}
                        passingScore={section.passing_score || 70}
                        gradingType={section.grading_type || 'auto'}
                        isAdmin={isAdmin}
                    />
                );
            case 'training':
                return (
                    <TrainingViewer
                        content={content}
                        onComplete={() => handleSectionComplete(section.id, { viewed: true })}
                        isSubmitting={isSubmitting}
                        isCompleted={isCompleted}
                        previousResponse={previousResponse}
                        isAdmin={isAdmin}
                    />
                );
            case 'checklist':
                return (
                    <ChecklistViewer
                        content={content}
                        onComplete={(checkedItems) => handleSectionComplete(section.id, { checkedItems })}
                        isSubmitting={isSubmitting}
                        isCompleted={isCompleted}
                        previousResponse={previousResponse}
                        isAdmin={isAdmin}
                    />
                );
            case 'form_fields': {
                const formResponses = sectionResponses[section.id]?.responses || previousResponse?.responses || {};
                return (
                    <FormFieldsSectionViewer
                        content={content}
                        formResponses={formResponses}
                        onResponsesChange={(responses) => handleResponseChange(section.id, { ...sectionResponses[section.id], responses })}
                        onComplete={(data) => handleSectionComplete(section.id, data)}
                        isAdmin={isAdmin}
                        isCompleted={isCompleted}
                        isSubmitting={isSubmitting}
                    />
                );
            }
            case 'content':
                return (
                    <div className="space-y-4">
                        <div className="prose dark:prose-invert max-w-none prose-sm text-gray-700 dark:text-slate-300">
                            <div dangerouslySetInnerHTML={{ __html: content || section.content }} />
                        </div>
                        {!isAdmin && !isCompleted && (
                            <div className="flex justify-end">
                                <button
                                    onClick={() => handleSectionComplete(section.id, { viewed: true })}
                                    disabled={isSubmitting}
                                    className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 transition-colors flex items-center gap-2"
                                >
                                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                                    Continue
                                </button>
                            </div>
                        )}
                        {!isAdmin && isCompleted && (
                            <div className="flex justify-end">
                                <span className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium">
                                    <Check className="w-4 h-4" /> Read
                                </span>
                            </div>
                        )}
                    </div>
                );
            case 'video': {
                const videoContent = typeof content === 'object' ? content : { url: content || '' };
                const videoUrl = videoContent.url || '';
                const youtubeMatch = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
                const loomMatch = videoUrl.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
                return (
                    <div className="space-y-3">
                        <div className="rounded-lg overflow-hidden bg-black">
                            {youtubeMatch ? (
                                <iframe src={`https://www.youtube.com/embed/${youtubeMatch[1]}`} className="w-full aspect-video" allowFullScreen title={section.title} />
                            ) : vimeoMatch ? (
                                <iframe src={`https://player.vimeo.com/video/${vimeoMatch[1]}`} className="w-full aspect-video" allowFullScreen title={section.title} />
                            ) : loomMatch ? (
                                <iframe src={`https://www.loom.com/embed/${loomMatch[1]}`} className="w-full aspect-video" allowFullScreen title={section.title} />
                            ) : videoUrl ? (
                                <video src={videoUrl} controls className="w-full aspect-video" />
                            ) : (
                                <div className="w-full aspect-video flex items-center justify-center bg-gray-100 dark:bg-slate-800">
                                    <p className="text-gray-400 dark:text-slate-500 text-sm">No video URL provided</p>
                                </div>
                            )}
                        </div>
                        {videoContent.caption && (
                            <p className="text-sm text-gray-500 dark:text-slate-400 text-center">{videoContent.caption}</p>
                        )}
                    </div>
                );
            }
            case 'divider': {
                const dividerContent = typeof content === 'object' ? content : {};
                const dividerStyle = dividerContent.style || 'solid';
                const dividerLabel = dividerContent.label || '';
                return (
                    <div className="py-2">
                        <div className="flex items-center gap-4">
                            <div className="flex-1 border-t border-gray-200 dark:border-slate-700" style={{ borderStyle: dividerStyle }} />
                            {dividerLabel && (
                                <>
                                    <span className="text-xs font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wider">{dividerLabel}</span>
                                    <div className="flex-1 border-t border-gray-200 dark:border-slate-700" style={{ borderStyle: dividerStyle }} />
                                </>
                            )}
                        </div>
                    </div>
                );
            }
            case 'callout': {
                const calloutContent = typeof content === 'object' ? content : { type: 'info', message: content || '' };
                const calloutType = calloutContent.type || 'info';
                const calloutStyles = {
                    info: { bg: 'bg-blue-50 dark:bg-blue-500/10', border: 'border-blue-200 dark:border-blue-500/20', text: 'text-blue-700 dark:text-blue-300', Icon: Info },
                    tip: { bg: 'bg-green-50 dark:bg-green-500/10', border: 'border-green-200 dark:border-green-500/20', text: 'text-green-700 dark:text-green-300', Icon: Lightbulb },
                    warning: { bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-200 dark:border-amber-500/20', text: 'text-amber-700 dark:text-amber-300', Icon: AlertTriangle },
                    important: { bg: 'bg-red-50 dark:bg-red-500/10', border: 'border-red-200 dark:border-red-500/20', text: 'text-red-700 dark:text-red-300', Icon: AlertTriangle }
                };
                const cs = calloutStyles[calloutType] || calloutStyles.info;
                const CalloutIcon = cs.Icon;
                return (
                    <div className={`flex items-start gap-3 p-4 rounded-lg border ${cs.bg} ${cs.border}`}>
                        <CalloutIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cs.text}`} />
                        <div className={`text-sm ${cs.text} whitespace-pre-wrap`}>{calloutContent.message}</div>
                    </div>
                );
            }
            case 'heading': {
                const headingContent = typeof content === 'object' ? content : { text: content || '', level: 'h2' };
                const level = headingContent.level || 'h2';
                return (
                    <div>
                        {level === 'h1' && <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{headingContent.text}</h1>}
                        {level === 'h2' && <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{headingContent.text}</h2>}
                        {level === 'h3' && <h3 className="text-lg font-medium text-gray-900 dark:text-white">{headingContent.text}</h3>}
                        {headingContent.subtitle && <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{headingContent.subtitle}</p>}
                    </div>
                );
            }
            case 'proof_upload': {
                const currentFiles = sectionResponses[section.id]?.proof_files || previousResponse?.proof_files || [];
                return (
                    <div className="space-y-4">
                        {content?.instructions && (
                            <p className="text-sm text-gray-600 dark:text-slate-400">{content.instructions}</p>
                        )}
                        <ProofFileUpload
                            proofFiles={currentFiles}
                            onProofFilesChange={(files) => handleResponseChange(section.id, { ...sectionResponses[section.id], proof_files: files })}
                            isSubmitting={isSubmitting}
                            readOnly={isAdmin || isCompleted}
                        />
                        {!isAdmin && (
                            <div className="flex justify-end">
                                {isCompleted ? (
                                    <span className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium">
                                        <Check className="w-4 h-4" /> Uploaded
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => handleSectionComplete(section.id, { proof_files: currentFiles, uploaded_at: new Date().toISOString() })}
                                        disabled={isSubmitting || !currentFiles || currentFiles.length === 0}
                                        className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                    >
                                        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        Submit Proof
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
            case 'signature': {
                const signatureName = sectionResponses[section.id]?.signature || previousResponse?.signature || '';
                const signatureAuditData = previousResponse?.signature_audit || null;
                return (
                    <SignatureSectionViewer
                        content={content}
                        signatureData={signatureName}
                        signatureAudit={signatureAuditData}
                        onSignatureChange={(sig) => handleResponseChange(section.id, { ...sectionResponses[section.id], signature: sig })}
                        onComplete={(data) => handleSectionComplete(section.id, data)}
                        isAdmin={isAdmin}
                        isCompleted={isCompleted}
                        isSubmitting={isSubmitting}
                        signerInfo={{
                            name: employeeInfo?.first_name && employeeInfo?.last_name
                                ? `${employeeInfo.first_name} ${employeeInfo.last_name}`
                                : employeeInfo?.name || 'Unknown',
                            email: employeeInfo?.email || 'Unknown'
                        }}
                    />
                );
            }
            default:
                return <p className="text-gray-500 dark:text-slate-400 text-sm">Unknown section type</p>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
        );
    }

    if (sections.length === 0) {
        return (
            <div className="text-center py-16 text-gray-400 dark:text-slate-500 text-sm">
                No sections found for this task.
            </div>
        );
    }

    // Decorative section types that render without the block wrapper
    const isDecorativeSection = (type) => ['divider', 'heading', 'callout'].includes(type);

    // PREVIEW MODE — simplified overview for employees
    if (previewMode && !isAdmin) {
        return (
            <div className="max-w-3xl mx-auto space-y-3">
                {sections.map((section, index) => {
                    const SectionIcon = SECTION_ICONS[section.section_type] || FileText;
                    const meta = getSectionMeta(section);

                    return (
                        <div
                            key={section.id || index}
                            className="flex items-center gap-4 px-5 py-4 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700/60 rounded-xl"
                        >
                            <div className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-slate-600 flex items-center justify-center flex-shrink-0">
                                <Circle className="w-3 h-3 text-gray-300 dark:text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 dark:text-white text-[15px]">{section.title}</p>
                                {meta && <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{meta}</p>}
                            </div>
                            {section.is_required && (
                                <span className="text-[10px] uppercase font-bold text-gray-400 dark:text-slate-500">Required</span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    const completedCount = sections.filter(s => isSectionCompleted(s.id)).length;
    const totalRequired = sections.filter(s => s.is_required).length;
    const completedRequired = sections.filter(s => s.is_required && isSectionCompleted(s.id)).length;
    const progressPercent = sections.length > 0 ? Math.round((completedCount / sections.length) * 100) : 0;

    return (
        <div className="space-y-4">
            {/* Sections */}
            {sections.map((section, index) => {
                const isCompleted = isSectionCompleted(section.id);
                const isLocked = isSectionLocked(index);
                const isCollapsed = !!collapsedSections[section.id];
                const SectionIcon = SECTION_ICONS[section.section_type] || FileText;
                const meta = getSectionMeta(section);

                const toggleCollapse = () => {
                    setCollapsedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }));
                };

                // Decorative sections render inline without block wrapper
                if (isDecorativeSection(section.section_type)) {
                    return (
                        <div key={section.id || index} className="px-2">
                            {renderSectionContent(section)}
                        </div>
                    );
                }

                return (
                    <div
                        key={section.id || index}
                        className={`rounded-xl border transition-all ${
                            isCompleted
                                ? 'border-green-200 dark:border-green-500/20 bg-green-50/30 dark:bg-green-500/5'
                                : isLocked
                                    ? 'border-gray-200 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/30 opacity-60'
                                    : 'border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900'
                        }`}
                    >
                        {/* Block Header */}
                        <button
                            onClick={toggleCollapse}
                            disabled={isLocked}
                            className="w-full flex items-center gap-4 px-5 py-4 text-left"
                        >
                            {/* Status Circle */}
                            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                isCompleted
                                    ? 'border-green-400 dark:border-green-500 bg-green-400 dark:bg-green-500'
                                    : isLocked
                                        ? 'border-gray-200 dark:border-slate-600'
                                        : 'border-gray-300 dark:border-slate-500'
                            }`}>
                                {isCompleted ? (
                                    <Check className="w-4 h-4 text-white" />
                                ) : isLocked ? (
                                    <Lock className="w-3 h-3 text-gray-300 dark:text-slate-500" />
                                ) : (
                                    <Circle className="w-3 h-3 text-gray-300 dark:text-slate-500" />
                                )}
                            </div>

                            {/* Title */}
                            <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-[15px] ${
                                    isCompleted
                                        ? 'text-green-800 dark:text-green-300'
                                        : isLocked
                                            ? 'text-gray-400 dark:text-slate-500'
                                            : 'text-gray-900 dark:text-white'
                                }`}>
                                    {section.title}
                                </p>
                                {meta && (
                                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{meta}</p>
                                )}
                            </div>

                            {/* Right side indicators */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {isCompleted && (
                                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">Done</span>
                                )}
                                {!isLocked && (
                                    <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-500 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                                )}
                            </div>
                        </button>

                        {/* Block Content */}
                        {!isCollapsed && !isLocked && (
                            <div className="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-slate-700/40">
                                {section.description && (
                                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">{section.description}</p>
                                )}
                                {renderSectionContent(section)}
                            </div>
                        )}

                        {/* Locked message */}
                        {!isCollapsed && isLocked && (
                            <div className="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-slate-700/40">
                                <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-6">
                                    Complete previous required sections to unlock
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CombinedTaskViewer;
